import { apiClient } from '../api/client'
import { User, EntityRole, PreferredLanguage } from '../types/user'

export type { User, EntityRole, PreferredLanguage } from '../types/user'

export interface SimpleUser {
  id: string
  email: string
  first_name: string
  last_name: string
  status: 'active' | 'inactive' | 'pending'
}

export interface InviteUserData {
  email: string
  firstName?: string
  lastName?: string
  entityRoles?: Array<{ entityCode: string; roles: string[] }>
  invitedBy?: string
}

export interface UpdateRolesData {
  entityRoles: Array<{
    entity_id: string
    roles: string[]
  }>
}

class UserService {
  async getUsers(): Promise<User[]> {
    return apiClient.get('/users')
  }

  async getUsersByRole(
    entityId: string,
    role: 'contributor' | 'approver' | 'legalRepresentative'
  ): Promise<SimpleUser[]> {
    const queryParams = new URLSearchParams({
      entity_id: entityId,
      role
    })
    return apiClient.get(`/users/by-role?${queryParams.toString()}`)
  }

  async getCurrentUser(): Promise<User> {
    return apiClient.get('/users/me')
  }

  async updatePreferredLanguage(preferred_language: PreferredLanguage): Promise<User> {
    return apiClient.patch('/users/me', { preferred_language })
  }

  async inviteUser(data: InviteUserData) {
    return apiClient.post('/users/invite', data)
  }

  async updateUserRoles(userId: string, data: UpdateRolesData) {
    return apiClient.put(`/users/${userId}/roles`, data)
  }

  async deactivateUser(userId: string) {
    return apiClient.post(`/users/${userId}/deactivate`)
  }

  async activateUser(userId: string) {
    return apiClient.post(`/users/${userId}/activate`)
  }

  /**
   * Generate a one-time login URL for a target user (admin only).
   * Returns the magic link URL without sending an email.
   * Use case: testing multi-user workflows, demos, support.
   */
  async generateLoginLink(userId: string): Promise<{ url: string }> {
    return apiClient.post(`/users/${userId}/generate-login-link`)
  }

  async resendInvitation(userId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post(`/users/${userId}/resend-invitation`)
  }

  groupRolesByEntity(roles: EntityRole[]): Map<string, string[]> {
    const grouped = new Map<string, string[]>()
    for (const role of roles) {
      const existing = grouped.get(role.entity_id) || []
      existing.push(role.role)
      grouped.set(role.entity_id, existing)
    }
    return grouped
  }
}

export const userService = new UserService()
